<template>
  <component :is="iconComponent" v-bind="$attrs" :size="size" :color="color" :stroke-width="strokeWidth" />
</template>

<script setup>
import { computed, defineAsyncComponent, h } from 'vue'

/**
 * Lucide Icon 图标组件
 *
 * @description 基于 lucide-vue-next 的统一图标组件，提供简约的线性风格图标
 *
 * @example 基础用法
 * <Icon name="User" />
 *
 * @example 自定义大小
 * <Icon name="Settings" :size="24" />
 *
 * @example 自定义颜色
 * <Icon name="Home" color="#409EFF" />
 *
 * @example 调整线条粗细
 * <Icon name="Search" :stroke-width="2.5" />
 */
const props = defineProps({
  /**
   * 图标名称
   * @see https://lucide.dev/icons/ 查看所有可用图标
   */
  name: {
    type: String,
    required: true
  },
  /**
   * 图标大小（像素）
   * @default 20
   */
  size: {
    type: [Number, String],
    default: 20
  },
  /**
   * 图标颜色
   * @default currentColor
   */
  color: {
    type: String,
    default: 'currentColor'
  },
  /**
   * 线条粗细
   * @default 2
   */
  strokeWidth: {
    type: [Number, String],
    default: 2
  }
})

// 图标组件缓存，避免重复创建
const iconCache = new Map()

// 动态导入图标组件
const iconComponent = computed(() => {
  const iconName = props.name

  // 检查缓存
  if (iconCache.has(iconName)) {
    return iconCache.get(iconName)
  }

  // 创建异步组件
  const asyncIcon = defineAsyncComponent({
    loader: () =>
      import('lucide-vue-next').then(module => {
        if (module[iconName]) {
          return module[iconName]
        }
        console.warn(`Icon "${iconName}" not found in lucide-vue-next`)
        // 返回一个默认的错误图标
        return {
          name: 'MissingIcon',
          render() {
            return h('span', {
              style: {
                display: 'inline-block',
                width: props.size + 'px',
                height: props.size + 'px',
                background: '#f0f0f0',
                borderRadius: '4px'
              }
            }, '?')
          }
        }
      }),
    loadingComponent: {
      render() {
        return h('span', { style: 'display:inline-block;width:20px;height:20px;background:#f5f5f5;border-radius:4px;' })
      }
    },
    errorComponent: {
      render() {
        return h('span', { style: 'display:inline-block;width:20px;height:20px;background:#fee;border-radius:4px;' }, '!')
      }
    }
  })

  // 缓存组件
  iconCache.set(iconName, asyncIcon)

  return asyncIcon
})
</script>

<script>
export default {
  name: 'LucideIcon',
  inheritAttrs: false
}
</script>
